import React from "react";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Alert,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    FormFeedback,
    Label,
} from "reactstrap";
import DatePicker from "react-datepicker";
import * as Yup from "yup";
import { infoIds } from "../../../../constants";
import { pagadoresService } from "../../services/pagadores";
import ConfirmationModal from "../../../../components/Modal/ConfirmationModal";

/**
 * Schema de validación Yup para el pagador
 */
const pagadorSchema = Yup.object().shape({
    participacion: Yup.number()
        .transform((value, originalValue) =>
            String(originalValue).trim() === "" ? undefined : value
        )
        .typeError("Debe ser un número válido")
        .required("Participación es requerida")
        .positive("Debe ser mayor a 0")
        .max(100, "Máximo 100"),
    codTipodocumento: Yup.string().required("Seleccione tipo de documento"),
    identificacion: Yup.string()
        .required("Campo requerido")
        .when("codTipodocumento", {
            is: (val) => val === infoIds.codigoRucStr,
            then: (schema) => schema.matches(/^\d{13}$/, "RUC debe tener 13 dígitos"),
            otherwise: (schema) =>
                schema.when("codTipodocumento", {
                    is: (val) => val === infoIds.codigoCedulaStr,
                    then: (schema) =>
                        schema.matches(/^\d{10}$/, "Cédula debe tener 10 dígitos"),
                }),
        }),
    cliNombres: Yup.string().trim().required("Nombres requeridos"),
    cliApellidos: Yup.string().trim().when("codTipodocumento", {
        is: (val) => val !== infoIds.codigoRucStr,
        then: (schema) => schema.required("Apellidos requeridos"),
        otherwise: (schema) => schema.nullable(),
    }),
    correo: Yup.string().trim().email("Correo inválido").required("Correo requerido"),
    direccion: Yup.string().trim().required("Dirección requerida"),
    celular: Yup.string()
        .required("Celular requerido")
        .matches(/^\d{10}$/, "Celular debe tener 10 dígitos"),
    fechaNacimiento: Yup.date()
        .nullable()
        .when("codTipodocumento", {
            is: (val) => val !== infoIds.codigoPasaporteStr,
            then: (schema) =>
                schema.required("Fecha de nacimiento/constitución requerida"),
        }),
});

/**
 * Retorna un objeto vacío de pagador
 */
const getEmptyPagador = () => ({
    codTipodocumento: "",
    identificacion: "",
    cliNombres: "",
    cliApellidos: "",
    fechaNacimiento: null,
    correo: "",
    direccion: "",
    celular: "",
    participacion: "",
});

/**
 * Lista de tipos de documento disponibles
 */
const getTipoDocumentos = () => [
    { id: "", name: "Seleccione..." },
    { id: infoIds.codigoCedulaStr, name: "Cédula" },
    { id: infoIds.codigoRucStr, name: "Ruc" },
    { id: infoIds.codigoPasaporteStr, name: "Pasaporte" },
];

/**
 * Componente GestionPagadores
 * 
 * Props esperados:
 * - idCarrito: string - ID del carrito actual
 * - listaPagadores: Array - Lista de pagadores actual
 * - totalParticipacion: number - Suma de participaciones
 * - loadingPagadores: boolean - Estado de carga
 * - onPagadoresChange: function(listaPagadores, totalParticipacion) - Callback cuando cambian los pagadores
 * - onLoadingChange: function(boolean) - Callback cuando cambia el estado de carga
 * - consultarIdentificacion: function - Redux action para buscar identificación
 * - datosIdentificacion: object - Datos de redux para identificación
 * - cambiarStringaFecha: function - Utilidad para convertir string a fecha
 */
class GestionPagadores extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            pagadorActual: getEmptyPagador(),
            pagadorModalOpen: false,
            editandoIndex: -1,
            errorsPagador: {},
            confirmModalOpen: false,
            payerToDeleteIndex: null,
            searchingId: false,
            savingPagador: false,
            alertMessage: "",
            alertType: "",
        };
        this.alertTimer = null;
    }

    componentDidMount() {
        this.cargarPagadores();
    }

    componentWillUnmount() {
        if (this.alertTimer) clearTimeout(this.alertTimer);
    }

    /**
     * Muestra una alerta temporal que se auto-oculta después de 5 segundos
     */
    showAlert = (message, type = "success") => {
        if (this.alertTimer) clearTimeout(this.alertTimer);
        this.setState({ alertMessage: message, alertType: type });
        this.alertTimer = setTimeout(() => {
            this.setState({ alertMessage: "", alertType: "" });
        }, 5000);
    };

    /**
     * Carga la lista de pagadores desde el backend
     */
    cargarPagadores = async () => {
        const { renovacionId, onPagadoresChange, onLoadingChange } = this.props;
        if (!renovacionId) return;

        onLoadingChange(true);
        try {
            const pagadores = await pagadoresService.obtenerPagadores(renovacionId);
            const listaPagadores = Array.isArray(pagadores) ? pagadores : [];
            const totalParticipacion = this.calcularTotalDesdeArray(listaPagadores);
            onPagadoresChange(listaPagadores, totalParticipacion);
            onLoadingChange(false);
        } catch (error) {
            console.error("Error al cargar pagadores:", error);
            onLoadingChange(false);
        }
    };

    /**
     * Calcula el total de participación desde un array de pagadores
     */
    calcularTotalDesdeArray = (pagadores) => {
        const total = pagadores.reduce(
            (acc, curr) => acc + parseFloat(curr.participacion || 0),
            0
        );
        return parseFloat(total.toFixed(2));
    };

    /**
     * Calcula la participación disponible para asignar
     */
    getParticipacionDisponible = () => {
        const { listaPagadores } = this.props;
        const { editandoIndex } = this.state;
        let totalAsignado = listaPagadores.reduce((acc, curr, index) => {
            if (index === editandoIndex) return acc;
            return acc + parseFloat(curr.participacion || 0);
        }, 0);

        let disponible = 100 - totalAsignado;
        return parseFloat(disponible.toFixed(2));
    };

    /**
     * Toggle del modal de pagador
     */
    toggleModal = () => {
        this.setState({
            pagadorModalOpen: !this.state.pagadorModalOpen,
            pagadorActual: this.state.pagadorModalOpen
                ? this.state.pagadorActual
                : getEmptyPagador(),
            editandoIndex: -1,
            errorsPagador: {},
        });
    };

    /**
     * Maneja cambios en los campos del formulario de pagador
     */
    handlePagadorChange = (field, value) => {
        this.setState(
            (prevState) => {
                // Limpiar el error del campo que se está modificando
                const updatedErrors = { ...prevState.errorsPagador };
                delete updatedErrors[field];

                return {
                    pagadorActual: {
                        ...prevState.pagadorActual,
                        [field]: value,
                    },
                    errorsPagador: updatedErrors,
                };
            },
            () => {
                // Auto-search logic
                if (field === "identificacion") {
                    const { codTipodocumento, identificacion } = this.state.pagadorActual;
                    if (
                        codTipodocumento === infoIds.codigoCedulaStr &&
                        identificacion.length === 10
                    ) {
                        this.buscarIdentificacionPagador();
                    } else if (
                        codTipodocumento === infoIds.codigoRucStr &&
                        identificacion.length === 13
                    ) {
                        this.buscarIdentificacionPagador();
                    }
                }
            }
        );
    };

    /**
     * Abre el modal para agregar un nuevo pagador
     */
    agregarPagador = () => {
        const { totalParticipacion } = this.props;
        if (totalParticipacion >= 100) {
            this.showAlert(
                "La participación ya es del 100%. Para agregar un nuevo pagador, elimine uno existente o modifique la participación de los actuales.",
                "warning"
            );
            return;
        }
        this.setState({
            pagadorActual: getEmptyPagador(),
            editandoIndex: -1,
            pagadorModalOpen: true,
            errorsPagador: {},
        });
    };

    /**
     * Abre el modal para editar un pagador existente
     */
    editarPagador = (index) => {
        const { listaPagadores } = this.props;
        this.setState({
            pagadorActual: { ...listaPagadores[index] },
            editandoIndex: index,
            pagadorModalOpen: true,
            errorsPagador: {},
        });
    };

    /**
     * Solicita confirmación para eliminar un pagador
     */
    solicitarEliminarPagador = (index) => {
        this.setState({ confirmModalOpen: true, payerToDeleteIndex: index });
    };

    /**
     * Confirma y ejecuta la eliminación de un pagador
     */
    confirmarEliminacion = async () => {
        const { payerToDeleteIndex } = this.state;
        const { listaPagadores, renovacionId, onLoadingChange } = this.props;

        if (payerToDeleteIndex !== null) {
            onLoadingChange(true);
            this.setState({ confirmModalOpen: false });
            const pagador = listaPagadores[payerToDeleteIndex];

            try {
                await pagadoresService.eliminarPagador(renovacionId, pagador.identificacion);
                await this.cargarPagadores();
                this.showAlert("Pagador eliminado correctamente.", "success");
            } catch (error) {
                console.error(error);
                this.showAlert("Error al eliminar pagador: " + error.message, "danger");
                onLoadingChange(false);
            }
            this.setState({ payerToDeleteIndex: null });
        } else {
            this.setState({ confirmModalOpen: false });
        }
    };

    /**
     * Guarda (crea o modifica) un pagador
     */
    guardarPagador = async () => {
        const { renovacionId, listaPagadores, onLoadingChange } = this.props;
        const { editandoIndex, pagadorActual, savingPagador } = this.state;

        // Prevenir doble clic
        if (savingPagador) return;

        try {
            await pagadorSchema.validate(pagadorActual, { abortEarly: false });

            if (pagadorActual.identificacion) {
                this.setState({ savingPagador: true });
                onLoadingChange(true);

                // Validar duplicados
                const existe = listaPagadores.some((p, index) => {
                    if (editandoIndex >= 0 && index === editandoIndex) return false;
                    return p.identificacion === pagadorActual.identificacion;
                });

                if (existe) {
                    this.setState({ savingPagador: false });
                    onLoadingChange(false);
                    this.setState({
                        errorsPagador: {
                            identificacion:
                                "El pagador con esta identificación ya existe en la lista.",
                        },
                    });
                    return;
                }
            } else {
                this.setState({ savingPagador: true });
                onLoadingChange(true);
            }

            // Validar que la participación no exceda el 100%
            const participacionIngresada = parseFloat(pagadorActual.participacion || 0);
            const participacionDisponible = this.getParticipacionDisponible();

            if (participacionIngresada > participacionDisponible) {
                this.setState({ savingPagador: false });
                onLoadingChange(false);
                this.setState({
                    errorsPagador: {
                        participacion: `La participación no puede exceder ${participacionDisponible}% (disponible).`,
                    },
                });
                return;
            }

            let response;
            if (editandoIndex >= 0) {
                response = await pagadoresService.modificarPagador(renovacionId, pagadorActual);
            } else {
                response = await pagadoresService.agregarPagador(renovacionId, pagadorActual);
            }

            if (response && response.transaccion === false) {
                const mensajeBackend = response.errorDescripcion || response.mensaje || "Error en la operación";
                throw new Error(mensajeBackend);
            }

            this.setState({
                pagadorModalOpen: false,
                pagadorActual: getEmptyPagador(),
                savingPagador: false,
            });

            await this.cargarPagadores();
            onLoadingChange(false);

            const accion = editandoIndex >= 0 ? "modificado" : "agregado";
            this.showAlert("Pagador " + accion + " correctamente.", "success");
        } catch (err) {
            this.setState({ savingPagador: false });
            onLoadingChange(false);
            if (err.inner) {
                const errors = {};
                err.inner.forEach((error) => {
                    errors[error.path] = error.message;
                });
                this.setState({ errorsPagador: errors });
            } else {
                this.setState({
                    errorsPagador: { general: err.message || "Error al guardar pagador." }
                });
            }
        }
    };

    /**
     * Busca datos de identificación del pagador en el backend
     */
    buscarIdentificacionPagador = async () => {
        const { identificacion, codTipodocumento } = this.state.pagadorActual;
        const { consultarIdentificacion, cambiarStringaFecha } = this.props;

        if (!identificacion) return;
        let tipo = "";
        if (codTipodocumento === infoIds.codigoRucStr) tipo = "ruc";
        if (codTipodocumento === infoIds.codigoCedulaStr) tipo = "cedula";
        if (!tipo) return;

        this.setState({ searchingId: true });
        try {
            await consultarIdentificacion({
                empresa: "AIGMETROPOLITANA",
                identificador: identificacion,
                tipo: tipo,
            });

            // Leer props actualizados después del await
            const { datosIdentificacion } = this.props;
            const data =
                datosIdentificacion &&
                datosIdentificacion.IdentificacionAP &&
                datosIdentificacion.IdentificacionAP[0];
            if (data) {
                this.setState((prevState) => ({
                    pagadorActual: {
                        ...prevState.pagadorActual,
                        cliNombres: data.nombres || data.nombre || "",
                        cliApellidos: data.apellidos || data.apellido || "",
                        direccion: data.direccion || "",
                        correo: data.correo || "",
                        celular: data.celular || "",
                        fechaNacimiento: data.fechNacimiento
                            ? cambiarStringaFecha(data.fechNacimiento)
                            : null,
                    },
                    searchingId: false,
                    errorsPagador: {},
                }));
            } else {
                this.setState({ searchingId: false });
            }
        } catch (error) {
            console.error("Error buscando identificación:", error);
            this.setState({ searchingId: false });
        }
    };

    /**
     * Maneja la tecla Enter en el modal para guardar
     */
    handleKeyDown = (e) => {
        if (e.key === "Enter" && !this.state.savingPagador) {
            e.preventDefault();
            this.guardarPagador();
        }
    };

    render() {
        const { listaPagadores, totalParticipacion } = this.props;
        const {
            pagadorModalOpen,
            pagadorActual,
            errorsPagador,
            editandoIndex,
            confirmModalOpen,
            savingPagador,
            searchingId,
            alertMessage,
            alertType,
        } = this.state;

        const tipoDocumentos = getTipoDocumentos();
        const btnStyle = { backgroundColor: "#001871", borderColor: "#001871", color: "#fff" };

        return (
            <>
                <Card className="flex-row mt-3">
                    <CardBody>
                        <Row className="align-items-center">
                            <Col>
                                <h4 style={{ fontWeight: "bold", marginBottom: 0 }}>
                                    <i className="fa fa-users" style={{ marginRight: 8, color: "#001871" }} />
                                    Gestión de Pagadores (Máximo {this.props.maxPagadores || 15})
                                </h4>
                            </Col>
                            <Col className="text-right">
                                <Button
                                    style={btnStyle}
                                    onClick={this.agregarPagador}
                                    disabled={listaPagadores.length >= (this.props.maxPagadores || 15)}
                                    title={listaPagadores.length >= (this.props.maxPagadores || 15) ? `Máximo ${this.props.maxPagadores || 15} pagadores` : ""}
                                >
                                    <i className="fa fa-plus" /> Agregar Pagador
                                </Button>
                            </Col>
                        </Row>

                        {alertMessage && (
                            <Alert
                                color={alertType}
                                className="mt-3"
                                toggle={() => this.setState({ alertMessage: "", alertType: "" })}
                            >
                                {alertMessage}
                            </Alert>
                        )}

                        <Table responsive hover striped className="mt-3">
                            <thead>
                                <tr>
                                    <th>N°</th>
                                    <th>Tipo Doc.</th>
                                    <th>Identificación</th>
                                    <th>Nombre</th>
                                    <th>Part. (%)</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listaPagadores.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center" style={{ padding: "2rem", color: "#999" }}>
                                            <i className="fa fa-info-circle" style={{ marginRight: 6 }} />
                                            No hay pagadores registrados. Haga clic en "Agregar Pagador" para comenzar.
                                        </td>
                                    </tr>
                                )}
                                {listaPagadores.map((p, i) => {
                                    const tipoDoc = tipoDocumentos.find(t => t.id === p.codTipodocumento);
                                    return (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{tipoDoc ? tipoDoc.name : p.codTipodocumento || "-"}</td>
                                            <td>{p.identificacion}</td>
                                            <td>
                                                {p.cliNombres}{p.cliApellidos && p.cliNombres && !p.cliNombres.includes(p.cliApellidos) ? (" " + p.cliApellidos) : ""}
                                            </td>
                                            <td>{p.participacion}%</td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <Button
                                                    size="sm"
                                                    color="info"
                                                    className="mr-2"
                                                    onClick={() => this.editarPagador(i)}
                                                    title="Editar pagador"
                                                >
                                                    <i className="fa fa-pencil" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    onClick={() => this.solicitarEliminarPagador(i)}
                                                    title="Eliminar pagador"
                                                >
                                                    <i className="fa fa-trash" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th colSpan="4" className="text-right">
                                        Total Participación:
                                    </th>
                                    <th>
                                        <span
                                            style={{
                                                color: totalParticipacion === 100 ? "green" : "red",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {totalParticipacion}%
                                        </span>
                                    </th>
                                    <th></th>
                                </tr>
                            </tfoot>
                        </Table>
                        {listaPagadores.length > 0 && totalParticipacion !== 100 && (
                            <Alert color="warning">
                                <i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} />
                                La participación total debe ser exactamente 100%. Actualmente: {totalParticipacion}%.
                            </Alert>
                        )}
                    </CardBody>
                </Card>

                {/* Modal de Confirmación para Eliminar */}
                <ConfirmationModal
                    title="Eliminar Pagador"
                    body="¿Está seguro que desea eliminar este pagador?"
                    modal={confirmModalOpen}
                    setModal={(open) => this.setState({ confirmModalOpen: open })}
                    buttons={[
                        {
                            title: "Eliminar",
                            onclick: this.confirmarEliminacion,
                            className: "btn-danger",
                            icon: "fa fa-trash",
                        },
                        {
                            title: "Cancelar",
                            onclick: () => this.setState({ confirmModalOpen: false }),
                            className: "btn-secondary",
                            icon: "fa fa-times",
                        },
                    ]}
                />

                {/* Modal para Agregar/Editar Pagador */}
                <Modal isOpen={pagadorModalOpen} toggle={this.toggleModal} size="lg">
                    <ModalHeader toggle={this.toggleModal}>
                        <i className={`fa ${editandoIndex >= 0 ? "fa-pencil" : "fa-plus-circle"}`} style={{ marginRight: 8 }} />
                        {editandoIndex >= 0 ? "Editar" : "Agregar"} Pagador
                    </ModalHeader>
                    <ModalBody>
                        <Row>
                            <Col md={6}>
                                <Label>Tipo de Identificación (*)</Label>
                                <Input
                                    type="select"
                                    value={pagadorActual.codTipodocumento}
                                    onChange={(e) =>
                                        this.handlePagadorChange("codTipodocumento", e.target.value)
                                    }
                                    invalid={!!errorsPagador.codTipodocumento}
                                    disabled={editandoIndex >= 0}
                                >
                                    {tipoDocumentos.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </Input>
                                <FormFeedback>{errorsPagador.codTipodocumento}</FormFeedback>
                            </Col>
                            <Col md={6}>
                                <Label>Número Identificación (*)</Label>
                                <Input
                                    value={pagadorActual.identificacion}
                                    onChange={(e) =>
                                        this.handlePagadorChange("identificacion", e.target.value)
                                    }
                                    onBlur={this.buscarIdentificacionPagador}
                                    invalid={!!errorsPagador.identificacion}
                                    disabled={editandoIndex >= 0 || searchingId}
                                    placeholder="Ingrese el número de identificación"
                                />
                                {searchingId && (
                                    <small className="text-muted">
                                        <i className="fa fa-spinner fa-spin" style={{ marginRight: 4 }} />
                                        Buscando...
                                    </small>
                                )}
                                {errorsPagador.identificacion && (
                                    <div className="invalid-feedback d-block">{errorsPagador.identificacion}</div>
                                )}
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={6}>
                                <Label>Nombres / Razón Social (*)</Label>
                                <Input
                                    value={pagadorActual.cliNombres}
                                    onChange={(e) =>
                                        this.handlePagadorChange("cliNombres", e.target.value)
                                    }
                                    invalid={!!errorsPagador.cliNombres}
                                />
                                <FormFeedback>{errorsPagador.cliNombres}</FormFeedback>
                            </Col>
                            {pagadorActual.codTipodocumento !== infoIds.codigoRucStr && (
                                <Col md={6}>
                                    <Label>Apellidos (*)</Label>
                                    <Input
                                        value={pagadorActual.cliApellidos}
                                        onChange={(e) =>
                                            this.handlePagadorChange("cliApellidos", e.target.value)
                                        }
                                        invalid={!!errorsPagador.cliApellidos}
                                    />
                                    <FormFeedback>{errorsPagador.cliApellidos}</FormFeedback>
                                </Col>
                            )}
                        </Row>
                        <Row className="mt-2">
                            <Col md={6}>
                                <Label>Correo (*)</Label>
                                <Input
                                    value={pagadorActual.correo}
                                    onChange={(e) =>
                                        this.handlePagadorChange("correo", e.target.value)
                                    }
                                    invalid={!!errorsPagador.correo}
                                />
                                <FormFeedback>{errorsPagador.correo}</FormFeedback>
                            </Col>
                            <Col md={6}>
                                <Label>Celular (*)</Label>
                                <Input
                                    value={pagadorActual.celular}
                                    onChange={(e) =>
                                        this.handlePagadorChange("celular", e.target.value)
                                    }
                                    invalid={!!errorsPagador.celular}
                                />
                                <FormFeedback>{errorsPagador.celular}</FormFeedback>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Label>Dirección (*)</Label>
                                <Input
                                    type="textarea"
                                    value={pagadorActual.direccion}
                                    onChange={(e) =>
                                        this.handlePagadorChange("direccion", e.target.value)
                                    }
                                    invalid={!!errorsPagador.direccion}
                                />
                                <FormFeedback>{errorsPagador.direccion}</FormFeedback>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={6}>
                                <Label>
                                    Fecha Nacimiento/Const.{" "}
                                    {pagadorActual.codTipodocumento !== infoIds.codigoPasaporteStr
                                        ? "(*)"
                                        : ""}
                                </Label>
                                <br />
                                <DatePicker
                                    className={`form-control ${errorsPagador.fechaNacimiento ? "is-invalid" : ""
                                        }`}
                                    selected={pagadorActual.fechaNacimiento}
                                    onChange={(date) =>
                                        this.handlePagadorChange("fechaNacimiento", date)
                                    }
                                    dateFormat="dd/MM/yyyy"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={80}
                                    scrollableYearDropdown
                                    maxDate={new Date()}
                                    placeholderText="dd/mm/aaaa"
                                />
                                {errorsPagador.fechaNacimiento && (
                                    <div className="invalid-feedback d-block">
                                        {errorsPagador.fechaNacimiento}
                                    </div>
                                )}
                            </Col>
                            <Col md={6}>
                                <Label>Participación % (*)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={pagadorActual.participacion}
                                    onChange={(e) =>
                                        this.handlePagadorChange("participacion", e.target.value)
                                    }
                                    invalid={!!errorsPagador.participacion}
                                    placeholder={`Max: ${this.getParticipacionDisponible()}%`}
                                />
                                <FormFeedback>{errorsPagador.participacion}</FormFeedback>
                                <small className="text-muted">
                                    Participación disponible: <strong>{this.getParticipacionDisponible()}%</strong>
                                </small>
                            </Col>
                        </Row>

                        {errorsPagador.general && (
                            <Row className="mt-3">
                                <Col md={12}>
                                    <Alert color="danger" className="mb-0">
                                        <i className="fa fa-exclamation-circle" style={{ marginRight: 6 }} />
                                        {errorsPagador.general}
                                    </Alert>
                                </Col>
                            </Row>
                        )}

                    </ModalBody>
                    <ModalFooter>
                        <Button
                            style={btnStyle}
                            onClick={this.guardarPagador}
                            disabled={savingPagador}
                        >
                            {savingPagador ? (
                                <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />Guardando...</>
                            ) : (
                                <><i className="fa fa-save" style={{ marginRight: 6 }} />Guardar</>
                            )}
                        </Button>
                        <Button color="secondary" onClick={this.toggleModal} disabled={savingPagador}>
                            Cancelar
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}

export default GestionPagadores;
